import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { formatApiError } from '@/lib/api';

export default function AdminPricing() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await api.get('/admin/pricing');
      setPricing(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setPricing({ ...pricing, [field]: parseFloat(value) || 0 });
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/pricing', pricing);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      alert(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C4033]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-pricing">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl text-[#1A1A1A]">Configuración de Precios</h1>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#5C4033] hover:bg-[#3D5A3E]"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Guardar Cambios
        </Button>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          Precios actualizados correctamente
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Hospedaje */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif">Hospedaje</CardTitle>
            <CardDescription>Precios por noche</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Precio por noche (Entre semana)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={pricing?.night_weekday || 0}
                  onChange={(e) => handleChange('night_weekday', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <Label>Precio por noche (Fin de semana)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={pricing?.night_weekend || 0}
                  onChange={(e) => handleChange('night_weekend', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Renta de Día */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif">Renta de Día</CardTitle>
            <CardDescription>Precios por día (9:00 AM - 6:00 PM)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Precio por día (Entre semana)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={pricing?.day_weekday || 0}
                  onChange={(e) => handleChange('day_weekday', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <Label>Precio por día (Fin de semana)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={pricing?.day_weekend || 0}
                  onChange={(e) => handleChange('day_weekend', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cargos Adicionales */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif">Cargos Adicionales</CardTitle>
            <CardDescription>Tarifas extras aplicables</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Tarifa de limpieza</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={pricing?.cleaning_fee || 0}
                  onChange={(e) => handleChange('cleaning_fee', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <Label>Cargo por huésped extra (más de {pricing?.base_guests || 6})</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={pricing?.extra_guest_fee || 0}
                  onChange={(e) => handleChange('extra_guest_fee', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <Label>Cargo por mascota</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={pricing?.pet_fee || 0}
                  onChange={(e) => handleChange('pet_fee', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <Label>Huéspedes base (sin cargo extra)</Label>
              <Input
                type="number"
                value={pricing?.base_guests || 0}
                onChange={(e) => handleChange('base_guests', e.target.value)}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Descuentos */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif">Descuentos por Estancia</CardTitle>
            <CardDescription>Porcentaje de descuento</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Descuento 3+ noches (%)</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  value={pricing?.discount_3_nights || 0}
                  onChange={(e) => handleChange('discount_3_nights', e.target.value)}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
            <div>
              <Label>Descuento 7+ noches (%)</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  value={pricing?.discount_7_nights || 0}
                  onChange={(e) => handleChange('discount_7_nights', e.target.value)}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
